<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <div v-if="seller" class="mb-6 mx-auto" style="max-width: 1200px;">
        <BanknoteAvatarDialog
          is-static
          show-upgrade
          show-downline-button
          downline-role="seller"
          :avatar-url="userAvatarUrl"
          :initials="userInitials"
          :pack-name="seller.commissionPack && seller.commissionPack.name"
          :user-ref="seller.commisionRefCode && seller.commisionRefCode.refCode"
          :referrer-ref="seller.referrer && seller.referrer.refCode"
          :referrer-name="seller.referrer && seller.referrer.name"
          :referrer-pack-name="seller.referrer && seller.referrer.packName"
          :alter-text="seller.alterText"
          @upgrade="openUpgradeDialog"
          @change="onAvatarClick"
          @save-alter="handleSaveAlter"
        />
      </div>

      <input type="file" ref="avatarInput" @change="handleAvatarChangeSelected" style="display:none" accept="image/*" />
      <AvatarCropperDialog :file="cropFile" @done="onCropDone" @cancel="cropFile = null" />

      <CommissionPackSection
        ref="packSection"
        :current-pack="seller.commissionPack || null"
        :wallet-balance="walletBalance"
        role="seller"
        style="display:none"
        @pack-changed="onPackChanged"
      />

      <v-card class="card-container d-flex pa-6" elevation="0">
        <v-row>
          <v-col cols="12">
            <span class="title-text">โปรไฟล์ของฉัน</span>
          </v-col>

          <v-col cols="12" v-if="seller">
            <SellerInfoCard
              :data="seller"
              @save="handleSaveProfile"
              @avatar-change="handleAvatarChange"
              @save-alter="handleSaveAlter"
            />
          </v-col>
          <v-col cols="12" v-if="seller" class="mt-4">
            <SellerFinancialCard
              :data="seller"
              :loading="savingProfile"
              @save="handleSaveProfile"
            />
          </v-col>

          <v-col cols="12" v-if="seller" class="mt-4">
            <SellerIdentityCard
              :data="seller"
              :loading="savingProfile"
              @save="handleSaveProfile"
            />
          </v-col>

          <v-col cols="12" v-if="seller" class="mt-4">
            <LocationPicker
              :initial-lat="seller.latitude"
              :initial-lng="seller.longitude"
              :initial-address="seller.addressData"
              @save="handleSaveLocation"
            />
          </v-col>

          <v-col cols="12" v-if="seller" class="mt-4">
            <ProfilePasswordCard
              ref="passwordCard"
              :loading="changingPassword"
              @change-password="handleChangePassword"
            />
          </v-col>

          <!-- คะแนนร้านค้า -->
          <v-col cols="12" v-if="seller" class="mt-4">
            <v-card class="card-container pa-6" elevation="0">
              <div class="d-flex align-center mb-4">
                <v-icon color="#F59E0B" class="mr-2">mdi-star</v-icon>
                <span class="title-text" style="font-size:18px;">คะแนนร้านค้า</span>
              </div>

              <!-- Summary -->
              <div class="d-flex align-center mb-5" v-if="sellerReviewCount > 0">
                <div class="text-center mr-6">
                  <div style="font-size:48px; font-weight:700; line-height:1; color:#B45309;">{{ sellerAvg.toFixed(1) }}</div>
                  <v-rating :value="sellerAvg" color="amber" dense half-increments readonly size="20" class="mt-1"></v-rating>
                  <div class="text-caption grey--text mt-1">{{ sellerReviewCount }} รีวิว</div>
                </div>
                <div class="flex-grow-1">
                  <div v-for="star in [5,4,3,2,1]" :key="star" class="d-flex align-center mb-1">
                    <span class="text-caption mr-2" style="min-width:16px;">{{ star }}★</span>
                    <v-progress-linear
                      :value="sellerReviewCount > 0 ? (ratingBreakdown[star-1] / sellerReviewCount) * 100 : 0"
                      color="amber"
                      background-color="#F3F4F6"
                      height="8"
                      rounded
                      class="flex-grow-1"
                    ></v-progress-linear>
                    <span class="text-caption grey--text ml-2" style="min-width:20px;">{{ ratingBreakdown[star-1] }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="text-body-2 grey--text mb-4">ยังไม่มีรีวิวจากลูกค้า</div>

              <!-- Review list -->
              <v-divider v-if="sellerReviews.length > 0" class="mb-4"></v-divider>
              <div v-for="rv in sellerReviews" :key="rv.id" class="mb-4">
                <div class="d-flex align-start">
                  <v-avatar size="36" color="#E8F5E9" class="mr-3">
                    <v-icon color="#198C19" small>mdi-account</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="d-flex align-center justify-space-between">
                      <span class="font-weight-bold text-body-2">{{ rv.reviewer?.username || rv.reviewer?.firstName || 'ผู้ใช้' }}</span>
                      <span class="text-caption grey--text">{{ formatReviewDate(rv.createdAt) }}</span>
                    </div>
                    <v-rating :value="rv.rating" color="amber" dense readonly size="14" class="my-1"></v-rating>
                    <div class="text-body-2 grey--text text--darken-1">{{ rv.comment || '—' }}</div>
                  </div>
                </div>
                <v-divider class="mt-3"></v-divider>
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            v-else
            class="d-flex flex-column align-center justify-center"
            style="min-height: 70vh"
          >
            <div class="d-flex flex-column align-center">
              <v-img>
                <img
                  src="@/assets/not_exist_search.svg"
                  alt="no-data"
                  width="103"
                  height="103"
                />
              </v-img>

              <span class="no-data-text mb-4">ไม่พบข้อมูลผู้ใช้</span>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import SellerInfoCard from "@/components/SellerProfile/SellerInfoCard.vue";
import CommissionPackSection from "@/components/Shared/CommissionPackSection.vue";
import BanknoteAvatarDialog from "@/components/Shared/BanknoteAvatarDialog.vue";
import AvatarCropperDialog from "@/components/Shared/AvatarCropperDialog.vue";
import ProfilePasswordCard from "@/components/AdminProfile/ProfilePasswordCard.vue";
import LocationPicker from "@/components/SellerProfile/LocationPicker.vue";
import SellerFinancialCard from "@/components/SellerProfile/SellerFinancialCard.vue";
import SellerIdentityCard from "@/components/SellerProfile/SellerIdentityCard.vue";
import { getSellerProfileApi } from "@/services/api/auth";
import { getSellerReviewsApi } from "@/services/api/review";
import {
  updateSellerProfileApi,
  updateSellerProfileImageApi,
  updateSellerPasswordApi,
} from "@/services/api/sellerServices";
import swalAlert from "@/services/alert/swalServices";
import walletService from "@/services/api/wallet";

const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})*$/;

export default {
  name: "SellerProfile",
  components: {
    SellerInfoCard,
    CommissionPackSection,
    BanknoteAvatarDialog,
    AvatarCropperDialog,
    ProfilePasswordCard,
    LocationPicker,
    SellerFinancialCard,
    SellerIdentityCard,
  },

  data() {
    return {
      isLoading: false,
      seller: null,
      walletBalance: 0,
      savingProfile: false,
      uploadingAvatar: false,
      changingPassword: false,
      sellerReviews: [],
      sellerAvg: 0,
      sellerReviewCount: 0,
      ratingBreakdown: [0, 0, 0, 0, 0],
      cropFile: null,
    };
  },

  computed: {
    userAvatarUrl() {
      if (!this.seller || !this.seller.storeImage) return "";
      const raw = this.seller.storeImage;
      if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
      const base = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
      return base + (raw.startsWith("/") ? raw : `/${raw}`);
    },
    userInitials() {
      if (!this.seller) return "";
      const name = this.seller.shopName || this.seller.username || "";
      return name.charAt(0).toUpperCase();
    },
  },

  created() {
    this.loadProfile();
    this.fetchWalletBalance();
  },

  methods: {
    async fetchWalletBalance() {
      try {
        const res = await walletService.getMyWallet("seller");
        this.walletBalance = res.data?.topUp?.balance ?? 0;
      } catch {
        this.walletBalance = 0;
      }
    },

    onPackChanged(pack) {
      if (this.seller) {
        this.seller = { ...this.seller, commissionPack: pack, commissionPackId: pack.id };
      }
      this.fetchWalletBalance();
    },

    openUpgradeDialog() {
      if (this.$refs.packSection) {
        this.$refs.packSection.openDialog();
      }
    },

    buildErrorMessage(err, fallback) {
      return (
        err?.response?.data?.message ||
        (err?.response?.status
          ? `เกิดข้อผิดพลาด (HTTP ${err.response.status})`
          : fallback)
      );
    },

    async loadProfile() {
      this.isLoading = true;
      try {
        const res = await getSellerProfileApi();
        this.seller = res?.seller || null;
        if (this.seller?.id) await this.loadReviews();
      } catch (err) {
        this.seller = null;
        console.error("Error loading profile:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadReviews() {
      try {
        const res = await getSellerReviewsApi(this.seller.id, { take: 5 });
        this.sellerReviews = res.data || [];
        this.sellerAvg = res.meta?.avg || 0;
        this.sellerReviewCount = res.meta?.count || 0;
        const breakdown = [0, 0, 0, 0, 0];
        this.sellerReviews.forEach(rv => {
          if (rv.rating >= 1 && rv.rating <= 5) breakdown[rv.rating - 1]++;
        });
        this.ratingBreakdown = breakdown;
      } catch (e) {
        console.error("[SellerProfile] load reviews failed:", e);
      }
    },

    formatReviewDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });
    },

    async handleSaveProfile(payload) {
      if (!this.seller) return;

      const phone = (payload.phone || "").trim();
      const email = (payload.email || "").trim();

      if (phone && !PHONE_REGEX.test(phone)) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากเบอร์โทรศัพท์ไม่ถูกต้อง"
        );
        return;
      }

      if (email && !EMAIL_REGEX.test(email)) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากรูปแบบอีเมลไม่ถูกต้อง (เช่น name@example.com)"
        );
        return;
      }

      const res = await swalAlert.Dialog(
        "ยืนยันการแก้ไขข้อมูล",
        "คุณต้องการบันทึกการเปลี่ยนแปลงข้อมูลโปรไฟล์หรือไม่?",
        require("@/assets/alert/save.png"),
        132,
        121,
        "บันทึก",
        "ยกเลิก",
        true
      );

      if (!res.isConfirmed) return;

      this.savingProfile = true;

      try {
        const body = {
          shopName: payload.shopName,
          phone,
          address: payload.address,
          promptPayId: payload.promptPayId,
          bankName: payload.bankName,
          bankAccountNumber: payload.bankAccountNumber,
          bankAccountName: payload.bankAccountName,
          natId_nationalId: payload.natId_nationalId,
          natId_prefix: payload.natId_prefix,
          natId_firstName: payload.natId_firstName,
          natId_lastName: payload.natId_lastName,
          natId_firstName_en: payload.natId_firstName_en,
          natId_lastName_en: payload.natId_lastName_en,
          natId_birthDate: payload.natId_birthDate,
          natId_issueDate: payload.natId_issueDate,
          natId_expireDate: payload.natId_expireDate,
        };

        // Remove undefined fields
        Object.keys(body).forEach(key => {
          if (body[key] === undefined) delete body[key];
        });

        if (payload.username && payload.username !== this.seller.username) {
          body.username = payload.username;
        }

        if (email && email !== this.seller.email) {
          body.email = email;
        }

        const apiRes = await updateSellerProfileApi(body);
        if (apiRes?.seller) this.seller = apiRes.seller;

        await swalAlert.Success("แก้ไขโปรไฟล์สำเร็จ");
      } catch (err) {
        swalAlert.Error(
          "แก้ไขโปรไฟล์ไม่สำเร็จ",
          this.buildErrorMessage(err, "ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้")
        );
      } finally {
        this.savingProfile = false;
      }
    },

    async handleSaveLocation(payload) {
      if (!this.seller) return;
      
      const res = await swalAlert.Dialog(
        "ยืนยันการบันทึกตำแหน่ง",
        "คุณต้องการบันทึกพิกัดร้านค้าใช่หรือไม่?",
        require("@/assets/alert/save.png"),
        132,
        121,
        "บันทึก",
        "ยกเลิก",
        true
      );
      
      if (!res.isConfirmed) return;
      
      this.isLoading = true;
      try {
        const apiRes = await updateSellerProfileApi({
          latitude: payload.lat,
          longitude: payload.lng,
          addressDetail: payload.addressDetail,
          subdistrictId: payload.subdistrictId,
          postalCode: payload.postalCode
        });
        if (apiRes?.seller) this.seller = apiRes.seller;
        await swalAlert.Success("บันทึกตำแหน่งร้านค้าสำเร็จ");
      } catch (err) {
        swalAlert.Error("บันทึกตำแหน่งไม่สำเร็จ", this.buildErrorMessage(err, "ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้"));
      } finally {
        this.isLoading = false;
      }
    },

    async handleSaveAlter(text) {
      try {
        const res = await updateSellerProfileApi({ alterText: text });
        if (res?.seller) this.seller = { ...this.seller, alterText: res.seller.alterText };
      } catch (err) {
        console.error("[SELLER SAVE ALTER]", err);
      }
    },

    async handleAvatarChange(file) {
      if (!file) return;
      this.uploadingAvatar = true;

      try {
        const res = await updateSellerProfileImageApi(file);
        if (res?.seller) this.seller = res.seller;

        await swalAlert.Success("แก้ไขรูปโปรไฟล์สำเร็จ");
      } catch (err) {
        swalAlert.Error(
          "อัปเดตรูปโปรไฟล์ไม่สำเร็จ",
          this.buildErrorMessage(
            err,
            "เกิดข้อผิดพลาดในการอัปโหลดรูป กรุณาลองใหม่อีกครั้ง"
          )
        );
      } finally {
        this.uploadingAvatar = false;
      }
    },

    onAvatarClick() {
      if (this.$refs.avatarInput) this.$refs.avatarInput.click();
    },

    handleAvatarChangeSelected(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.cropFile = file;
    },

    async onCropDone(croppedFile) {
      this.cropFile = null;
      await this.handleAvatarChange(croppedFile);
    },

    async handleChangePassword(payload) {
      const { currentPassword, newPassword, confirmPassword } = payload || {};

      if (!currentPassword || !newPassword || !confirmPassword) {
        await swalAlert.Error(
          "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          "กรุณากรอกข้อมูลรหัสผ่านให้ครบถ้วน"
        );
        return;
      }

      if (String(newPassword).length < 8) {
        await swalAlert.Error(
          "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          "รหัสผ่านใหม่ต้องยาวอย่างน้อย 8 ตัวอักษร"
        );
        return;
      }

      if (newPassword !== confirmPassword) {
        await swalAlert.Error(
          "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          "รหัสผ่านใหม่และยืนยันรหัสผ่านใหม่ไม่ตรงกัน"
        );
        return;
      }

      const res = await swalAlert.Dialog(
        "ยืนยันการเปลี่ยนรหัสผ่าน",
        "คุณต้องการเปลี่ยนรหัสผ่านใช่หรือไม่?",
        require("@/assets/alert/save.png"),
        132,
        121,
        "ยืนยัน",
        "ยกเลิก",
        true
      );

      if (!res.isConfirmed) return;

      this.changingPassword = true;

      try {
        const apiRes = await updateSellerPasswordApi({
          currentPassword,
          newPassword,
          confirmPassword,
        });

        const msg = apiRes?.message || "แก้ไขรหัสผ่านสำเร็จ";
        await swalAlert.Success(msg);

        if (this.$refs.passwordCard && this.$refs.passwordCard.reset) {
          this.$refs.passwordCard.reset();
        }
      } catch (err) {
        swalAlert.Error(
          "แก้ไขรหัสผ่านไม่สำเร็จ",
          this.buildErrorMessage(
            err,
            "ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาลองอีกครั้ง"
          )
        );
      } finally {
        this.changingPassword = false;
      }
    },
  },
};
</script>

<style scoped>
.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}
.no-data-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b717f;
}
.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}
</style>

